import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { trackList } from '@webapp/store/tracks/selectors';
import { assetsList } from '../../../store/assets/selectors';
import SimpleTrack from '@common/types/release/simple-track';
import { ButtonType } from '@components/actions/button/index';
import { AssetUpload } from '@webapp/types/assets/asset-upload';
import { normalizeTrackName } from '../../../utils/normalizers';
import { ReleaseType } from '@common/types/release/release-type';
import { CreateDialog } from '../../../store/ui/action-creators';
import { RemoveTrack } from '@webapp/store/tracks/action-creators';
import { UpdateTrackOrder } from '@webapp/store/tracks/action-creators';
import { TrackOrderUpdate } from '@services/track/types/track-order-update';
import { DialogConfiguration } from '../../../types/ui/dialog-configuration';
import { CancelAssetUpload } from '@webapp/store/assets/action-creators';

import { AssetsState } from '@webapp/store/assets/state';
import { AssetValidationResults } from '@services/asset/types/asset-validation-results';

import TrackUploadItems from '../track-upload-items';
import { Translate } from 'react-localize-redux';
import { SimpleRelease } from '@common/types/release/simple-release';

interface TrackUploadProps {
  className?: string;
  releaseId: string;
  tracks: Partial<SimpleTrack>[];
  statuses: AssetsState['statuses'];
  assets: AssetUpload[];
  titles: { id?: string; title: string }[];
  type?: ReleaseType;
  activeRelease?: SimpleRelease;
  artistId?: string;
  disabled?: boolean;
  location: any;
  trackingId?: string;
  updateOrder: (
    order: TrackOrderUpdate[],
    releaseId: string
  ) => UpdateTrackOrder;
  openModal: (trackId: string, callback: (value: any) => void) => CreateDialog;
  removeTrack: (releaseId: string, trackId: string) => RemoveTrack;
  goBack: () => CallHistoryMethodAction;
  cancelUpload: (trackingId: string, assetId: string) => CancelAssetUpload;

  disableRemove?: boolean;
  disableAddMore?: boolean;
  disableSorting?: boolean;
  routePrefix?: string;
}

const buildDialog = (
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: <Translate id="UPLOAD.TRACK.DELETE.MODAL.TITLE" />,
    text: <Translate id="UPLOAD.TRACK.DELETE.MODAL.TEXT" />,
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(
    state.tracks.tracks,
    state.release.release?.id as string
  );

  return {
    releaseId: state.release.release?.id as string,
    tracks,
    statuses: state.assets.statuses,
    titles: tracks.map((track) => ({
      id: track.id,
      title: normalizeTrackName(
        {
          ...track,
          trackVersion:
            track.trackVersion === 'Original' ? '' : track.trackVersion,
        },
        state
      ),
    })),
    activeRelease: state.release.release as SimpleRelease,
    assets: assetsList(state.assets),
    type: state.release.release?.type,
    artistId: state.artist.active?.id,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateOrder: (order: TrackOrderUpdate[], releaseId: string) =>
    dispatch(new UpdateTrackOrder(order, releaseId)),
  openModal: (trackId: string, callback: (value: any) => void) =>
    dispatch(new CreateDialog(trackId, buildDialog(callback))),
  removeTrack: (releaseId: string, trackId: string) =>
    dispatch(new RemoveTrack(releaseId, trackId)),
  goBack: () => dispatch(push('../title')),
  cancelUpload: (trackingId: string, assetId: string) =>
    dispatch(new CancelAssetUpload(trackingId, assetId)),
});

export const TrackUpload: React.FC<TrackUploadProps> = (props) => {
  const mergedTracks = props.tracks.map((track) => {
    const asset = props.assets.find((a) => a.id === track.id);
    const title = props.titles.find((t) => t.id === track.id);
    const status = track.id ? props.statuses[track.id] : undefined;

    return [track, asset, title?.title || track.title || '', status] as [
      SimpleTrack,
      AssetUpload | undefined,
      string,
      AssetValidationResults | undefined
    ];
  });

  const handleRemove = (trackId: string, isActive: boolean) => {
    props.openModal(trackId, (confirmation) => {
      if (confirmation) {
        if (isActive) {
          props.goBack();
        }
        props.removeTrack(props.releaseId as string, trackId);
      }
    });
  };

  return (
    <TrackUploadItems
      tracks={mergedTracks}
      releaseId={props.releaseId}
      activeRelease={props.activeRelease}
      artistId={props.artistId}
      location={props.location}
      disabled={props.disabled}
      updateOrder={(tracks) => props.updateOrder(tracks, props.releaseId || '')}
      onRemove={(trackId: string, isActive: boolean) =>
        handleRemove(trackId, isActive)
      }
      onCancel={(assetId) =>
        props.trackingId && assetId
          ? props.cancelUpload(props.trackingId, assetId)
          : null
      }
      disableRemove={props.disableRemove}
      disableSorting={props.disableSorting || !!props.activeRelease?.source}
      routePrefix={props.routePrefix}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackUpload);
